import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { classes } from "../../../../util/style-utils";
import useInsurersOptions from "../../../../hooks/useInsurersOptions";
import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { GeaoContext } from "../../GeaoContextProvider";
import { update } from "../../../../api/onboarding";
import GeaoVerticalStepperLayout from "../shared/Layout/GeaoVerticalStepperLayout";
import PrincipalFlowVerticalStepperElements from "../../constants/PrincipalFlowVerticalStepperElements";
import useTitle from "../../../../hooks/useTitle";
import { InputNumberFormat } from '@react-input/number-format';
import OptionsProvider from "../../../Options";
import { GeaoPaths } from "../../constants/GeaoPaths";
import { PageStatus } from "../../hooks/useInterviewUpdater";
import getFinancialInformationSchema from "../../validation/FinancialInformationSchema"

function interviewToFinancialInfoData(agencyInterview) {
    const q = agencyInterview?.questionAnswers || {};
    return {
        personalPercent: q.PercentAnnualPremimumPersonal?.answer ?? "",
        commercialPercent: q.PercentAnnualPremimumCommnercial?.answer ?? "",
        otherPercent: q.PercentAnnualPremimumOther?.answer ?? "",
        personalAutoCheck: !!(q.WrittenPremiumPersonal?.answer || q.PoliciesInForcePersonal?.answer),
        homeCheck: !!(q.WrittenPremiumHomeCondoRental?.answer || q.PoliciesInForceHomeCondoRental?.answer),
        recreationalCheck: !!(q.WrittenPremiumRecreational?.answer || q.PoliciesInForceRecreational?.answer),
        commercialAutoCheck: !!(q.WrittenPremiumCommercialAuto?.answer || q.PoliciesInForceCommercialAuto?.answer),
        commercialTruckingCheck: !!(q.WrittenPremiumCommercialTrucking?.answer || q.PoliciesInForceCommercialTrucking?.answer),
        generalCheck: !!(q.WrittenPremiumBOPGenLiability?.answer || q.PoliciesInForceBOPGenLiability?.answer),
        otherCheck: !!(q.WrittenPremiumOther?.answer || q.PoliciesInForceOther?.answer),
        personalAutoPremium: q.WrittenPremiumPersonal?.answer ?? "",
        homePremium: q.WrittenPremiumHomeCondoRental?.answer ?? "",
        recreationalPremium: q.WrittenPremiumRecreational?.answer ?? "",
        commercialAutoPremium: q.WrittenPremiumCommercialAuto?.answer ?? "",
        commercialTruckingPremium: q.WrittenPremiumCommercialTrucking?.answer ?? "",
        generalPremium: q.WrittenPremiumBOPGenLiability?.answer ?? "",
        otherPremium: q.WrittenPremiumOther?.answer ?? "",
        personalAutoPolicies: q.PoliciesInForcePersonal?.answer ?? "",
        homePolicies: q.PoliciesInForceHomeCondoRental?.answer ?? "",
        recreationalPolicies: q.PoliciesInForceRecreational?.answer ?? "",
        commercialAutoPolicies: q.PoliciesInForceCommercialAuto?.answer ?? "",
        commercialTruckingPolicies: q.PoliciesInForceCommercialTrucking?.answer ?? "",
        generalPolicies: q.PoliciesInForceBOPGenLiability?.answer ?? "",
        otherPolicies: q.PoliciesInForceOther?.answer ?? "",
        insurerOne: q.TopInsurer1Name?.answer ?? "",
        personalOne: q.TopInsurer1PercentagePersonal?.answer ?? "",
        commercialOne: q.TopInsurer1PercentageCommercial?.answer ?? "",
        insurerTwo: q.TopInsurer2Name?.answer ?? "",
        personalTwo: q.TopInsurer2PercentagePersonal?.answer ?? "",
        commercialTwo: q.TopInsurer2PercentageCommercial?.answer ?? "",
        insurerThree: q.TopInsurer3Name?.answer ?? "",
        personalThree: q.TopInsurer3PercentagePersonal?.answer ?? "",
        commercialThree: q.TopInsurer3PercentageCommercial?.answer ?? "",
        totalPremium: q.TotalAnnualWrittenPremium?.answer ?? "",
        totalPolicies: q.TotalPolicies?.answer ?? "",
    };
}

export default function PrincipalAgencyFinancialInformation() {
    useTitle("Financial Information");
    const { agencyInterview, setAgencyInterview, agentInterview, interviewIsCompleted } = useContext(GeaoContext);

    const isNewBusiness = () => agencyInterview?.questionAnswers?.["YearsInBusiness"]?.answer <= 1;
    const if_else = (a, b, c) => a ? b : c;

    const defaultValues = interviewToFinancialInfoData(agencyInterview);

    const { register, handleSubmit, trigger, getValues, formState: { errors } } = useForm({
        resolver: yupResolver(getFinancialInformationSchema(isNewBusiness)),
        defaultValues,
    });

    const navigate = useNavigate();

    const [personalAutoCheck, setPersonalAutoCheck] = useState(defaultValues.personalAutoCheck);
    const [homeCheck, setHomeCheck] = useState(defaultValues.homeCheck);
    const [recreationalCheck, setRecreationalCheck] = useState(defaultValues.recreationalCheck);
    const [commercialAutoCheck, setCommercialAutoCheck] = useState(defaultValues.commercialAutoCheck);
    const [commercialTruckingCheck, setCommercialTruckingCheck] = useState(defaultValues.commercialTruckingCheck);
    const [generalCheck, setGeneralCheck] = useState(defaultValues.generalCheck);
    const [otherCheck, setOtherCheck] = useState(defaultValues.otherCheck);

    const [totalPremium, setTotalPremium] = useState(0);
    const [totalPolicies, setTotalPolicies] = useState(0);
    const [buttonsDisabled, setButtonsDisabled] = useState(false);

    const insurersOptions = useInsurersOptions();

    function updateInterview(data, isNext, isSaveAndExit = false) {
        setButtonsDisabled(true);

        let activityRequest = null;
        if (isSaveAndExit) {
            activityRequest = {
                ActivityCategory: "GEAO_ONBOARDING",
                ActivitySubType: "SAVE_AND_EXIT",
                SourceApplication: "AGIWUI",
                AdditionalData: {
                    ComponentName: "PrincipalAgencyOfficeInformation"
                }
            };
        }

        const updatedAgentInterview = {
            ...agentInterview,
            pageStatus: {
                ...agentInterview.pageStatus,
                "PrincipalAgencyOfficeInformation": PageStatus.Completed
            }
        };

        update({
            identityType: "Agent",
            interview: updatedAgentInterview,
            activityRequest: activityRequest
        });

        const questionAnswers = {
            ...agencyInterview?.questionAnswers ?? null,
            TotalAnnualWrittenPremium: { answer: totalPremium ? totalPremium.toString().replaceAll(nonNumberRegex, '') : "" },
            PercentAnnualPremimumPersonal: { answer: data.personalPercent.toString() ?? "" },
            PercentAnnualPremimumCommnercial: { answer: data.commercialPercent.toString() ?? "" },
            PercentAnnualPremimumOther: { answer: data.otherPercent.toString() ?? "" },
            WrittenPremiumPersonal: { answer: data.personalAutoPremium ? data.personalAutoPremium.replaceAll(nonNumberRegex, '') : "" },
            PoliciesInForcePersonal: { answer: data.personalAutoPolicies ? data.personalAutoPolicies.replaceAll(nonNumberRegex, '') : "" },
            WrittenPremiumHomeCondoRental: { answer: data.homePremium ? data.homePremium.toString().replaceAll(nonNumberRegex, '') : "" },
            PoliciesInForceHomeCondoRental: { answer: data.homePolicies ? data.homePolicies.replaceAll(nonNumberRegex, '') : "" },
            WrittenPremiumRecreational: { answer: data.recreationalPremium ? data.recreationalPremium.replaceAll(nonNumberRegex, '') : "" },
            PoliciesInForceRecreational: { answer: data.recreationalPolicies ? data.recreationalPolicies.replaceAll(nonNumberRegex, '') : "" },
            WrittenPremiumCommercialAuto: { answer: data.commercialAutoPremium ? data.commercialAutoPremium.replaceAll(nonNumberRegex, '') : "" },
            PoliciesInForceCommercialAuto: { answer: data.commercialAutoPolicies ? data.commercialAutoPolicies.replaceAll(nonNumberRegex, '') : "" },
            WrittenPremiumCommercialTrucking: { answer: data.commercialTruckingPremium ? data.commercialTruckingPremium.replaceAll(nonNumberRegex, '') : "" },
            PoliciesInForceCommercialTrucking: { answer: data.commercialTruckingPolicies ? data.commercialTruckingPolicies.replaceAll(nonNumberRegex, '') : "" },
            WrittenPremiumBOPGenLiability: { answer: data.generalPremium ? data.generalPremium.replaceAll(nonNumberRegex, '') : "" },
            PoliciesInForceBOPGenLiability: { answer: data.generalPolicies ? data.generalPolicies.replaceAll(nonNumberRegex, '') : "" },
            WrittenPremiumOther: { answer: data.otherPremium ? data.otherPremium.replaceAll(nonNumberRegex, '') : "" },
            PoliciesInForceOther: { answer: data.otherPolicies ? data.otherPolicies.replaceAll(nonNumberRegex, '') : "" },
            TopInsurer1Name: { answer: data.insurerOne.toString() ?? "" },
            TopInsurer1PercentageCommercial: { answer: data.commercialOne.toString() ?? "" },
            TopInsurer1PercentagePersonal: { answer: data.personalOne.toString() ?? "" },
            TopInsurer2Name: { answer: data.insurerTwo.toString() ?? "" },
            TopInsurer2PercentageCommercial: { answer: data.commercialTwo.toString() ?? "" },
            TopInsurer2PercentagePersonal: { answer: data.personalTwo.toString() ?? "" },
            TopInsurer3Name: { answer: data.insurerThree.toString() ?? "" },
            TopInsurer3PercentageCommercial: { answer: data.commercialThree.toString() ?? "" },
            TopInsurer3PercentagePersonal: { answer: data.personalThree.toString() ?? "" },
        }

        const updatedInterview = {
            identityType: "Agency",
            interview: {
                ...agencyInterview,
                questionAnswers,
                pageStatus: {
                    ...agencyInterview.pageStatus,
                    "PrincipalAgencyFinancialInformation": PageStatus.Completed
                }
            }
        }

        update(updatedInterview,
            interview => {
                setAgencyInterview(interview);

                if (isNext) {
                    navigate(GeaoPaths.PrincipalAgencyAdditionalInformation);
                } else {
                    navigate(GeaoPaths.PrincipalWelcomeChecklist);
                }
            },
            () => {
                setButtonsDisabled(false);
            });
    }

    function onSubmit(values) {
        updateInterview(values, true);
    };

    function onSaveAndExit() {
        trigger().then(validationPassed => {
            if (validationPassed) {
                updateInterview(getValues(), false, true)
            }
        });
    }

    const numberFormatter = new Intl.NumberFormat();
    const nonNumberRegex = new RegExp("[^0-9.]", "g");

    const checkAndParse = (elementId, check) => {
        const element = document.getElementById(elementId);
        if (check && element && element.value !== "") {
            return parseFloat(element.value.replaceAll(nonNumberRegex, ''));
        }
        return 0;
    }

    const addPolicies = () => {
        let total = 0;

        total += checkAndParse("personalAutoPolicies", personalAutoCheck);
        total += checkAndParse("homePolicies", homeCheck);
        total += checkAndParse("recreationalPolicies", recreationalCheck);
        total += checkAndParse("commercialAutoPolicies", commercialAutoCheck);
        total += checkAndParse("commercialTruckingPolicies", commercialTruckingCheck);
        total += checkAndParse("generalPolicies", generalCheck);
        total += checkAndParse("otherPolicies", otherCheck);

        setTotalPolicies(numberFormatter.format(total));
    }

    const addPremiums = () => {
        let total = 0;

        total += checkAndParse("personalAutoPremium", personalAutoCheck);
        total += checkAndParse("homePremium", homeCheck);
        total += checkAndParse("recreationalPremium", recreationalCheck);
        total += checkAndParse("commercialAutoPremium", commercialAutoCheck);
        total += checkAndParse("commercialTruckingPremium", commercialTruckingCheck);
        total += checkAndParse("generalPremium", generalCheck);
        total += checkAndParse("otherPremium", otherCheck);

        setTotalPremium(numberFormatter.format(total));
    }

    useEffect(() => {
        addPolicies();
        addPremiums();
    }, []);

    const buttonStyle = {
        marginBottom: "3rem",
        marginTop: "3rem",
    };

    const tableHeaderStyle = {
        display: "flex",
        justifyContent: "space-around",
        backgroundColor: "#DCDCDC",
        " paddingLeft": "1rem",
    };
    const tableHeaderTextStyle = {
        marginTop: "12px",
        width: "32%",
    };

    const columnTextStyle = {
        width: "32%",
        fontWeight: "normal",
        alignContent: "center",
        paddingBottom: "15px",
    };

    const columnInputStyle = {
        width: "32%",
        height: "65px",
    };

    const tableRowStyle = {
        display: "flex",
        justifyContent: "space-between",
    };

    return (
        <GeaoVerticalStepperLayout
            verticalStepperElements={PrincipalFlowVerticalStepperElements}
            currentStepperItem="Financial Information"
            header="Financial Information"
        >
            <form className="geao-form" onSubmit={handleSubmit(onSubmit)}>
                <div className="row">
                    <div
                        style={{ width: "65%" }}
                        className={classes(
                            "form-field col-sm-6",
                            errors.personalPercent && "form-field--error"
                        )}
                    >
                        <label htmlFor="personalPercent" className="text">
                            {if_else(isNewBusiness(), "% of Forecasted Annual Premium From Personal Lines", "% of Annual Premium From Personal Lines")}{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input
                            id="personalPercent"
                            type="text"
                            style={{ width: "65%" }}
                            placeholder="%"
                            {...register("personalPercent")}
                        />
                        {errors.personalPercent && (
                            <span className="form-message">
                                {errors.personalPercent.message}
                            </span>
                        )}
                    </div>
                </div>
                <div className="row">
                    <div
                        style={{ width: "65%" }}
                        className={classes(
                            "form-field col-sm-6",
                            errors.commercialPercent && "form-field--error"
                        )}
                    >
                        <label htmlFor="commercialPercent" className="text">
                            {if_else(isNewBusiness(), "% of Forecasted Annual Premium From Commercial Lines", "% of Annual Premium From Commercial Lines")}{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input
                            id="commercialPercent"
                            type="text"
                            style={{ width: "65%" }}
                            placeholder="%"
                            {...register("commercialPercent")}
                        />
                        {errors.commercialPercent && (
                            <span className="form-message">
                                {errors.commercialPercent.message}
                            </span>
                        )}
                    </div>
                </div>
                <div className="row">
                    <div
                        style={{ width: "65%" }}
                        className={classes(
                            "form-field col-sm-6",
                            errors.otherPercent && "form-field--error"
                        )}
                    >
                        <label htmlFor="otherPercent" className="text">
                            {if_else(isNewBusiness(), "% of Forecasted Annual Premium From Other Lines", "% of Annual Premium From Other Lines")}{/*
                            */}<span className='required'>*</span>
                        </label>
                        <input
                            id="otherPercent"
                            type="text"
                            style={{ width: "65%" }}
                            placeholder="%"
                            {...register("otherPercent")}
                        />
                        {errors.otherPercent && (
                            <span className="form-message">
                                {errors.otherPercent.message}
                            </span>
                        )}
                    </div>
                </div>
                <label className="text">
                    {if_else(isNewBusiness(), "Forecasted Written Premium and Policies in Force by Line of Business", "Written Premium and Policies in Force by Line of Business, as of End of Prior Year")}
                </label>
                <p>Select the lines of business sold by your agency.</p>
                <div>
                    <div style={tableHeaderStyle}>
                        <label className="text" style={{ marginTop: "12px", width: "32%", paddingLeft: "20px" }}>
                            Line of Business
                        </label>
                        <label className="text" style={tableHeaderTextStyle}>
                            Written Premium
                        </label>
                        <label className="text" style={tableHeaderTextStyle}>
                            Policies in Force
                        </label>
                    </div>
                    <ul className="list list--one-column">
                        <li style={tableRowStyle}>
                            <fieldset className={classes(
                                "form-field checkbox-group old-style",
                                errors.personalAutoCheck && "form-field--error")}
                                role="radiogroup"
                                style={columnTextStyle}
                            >
                                <div className="checkbox-wrapper">
                                    <div>
                                        <input
                                            id="personalAutoCheck"
                                            type="checkbox"
                                            {...register("personalAutoCheck")}
                                            onChange={(v) => {
                                                setPersonalAutoCheck(v.target.checked)
                                                const premiums = document.getElementById("personalAutoPremium")
                                                const policies = document.getElementById("personalAutoPolicies")
                                                if (premiums && policies && v.target.checked === false) {
                                                    premiums.value = "";
                                                    policies.value = "";
                                                    addPolicies();
                                                    addPremiums();
                                                }
                                            }}
                                        />
                                        <label htmlFor="personalAutoCheck" className="checkbox">Personal Auto</label>
                                        {errors.personalAutoCheck && (
                                            <span className="form-message">
                                                {errors.personalAutoCheck.message}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </fieldset>
                            <div
                                style={columnInputStyle}
                                className={classes(
                                    "form-field",
                                    errors.personalAutoPremium && "form-field--error"
                                )}
                            >
                                <InputNumberFormat
                                    id="personalAutoPremium"
                                    type="text"
                                    style={{ width: "98%" }}
                                    placeholder="$"
                                    disabled={!personalAutoCheck}
                                    {...register("personalAutoPremium")}
                                    onBlur={addPremiums}
                                    locales="en-US"
                                    format="currency"
                                    currency="USD"
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={2}
                                />
                                {errors.personalAutoPremium && (
                                    <span className="form-message">
                                        {errors.personalAutoPremium.message}
                                    </span>
                                )}
                            </div>
                            <div
                                style={columnInputStyle}
                                className={classes(
                                    "form-field",
                                    errors.personalAutoPolicies && "form-field--error"
                                )}>
                                <InputNumberFormat
                                    id="personalAutoPolicies"
                                    type="text"
                                    style={{ width: "98%" }}
                                    disabled={!personalAutoCheck}
                                    {...register("personalAutoPolicies")}
                                    onBlur={addPolicies}
                                    locales="en-US"
                                    format="decimal"
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={0}
                                />
                                {errors.personalAutoPolicies && (
                                    <span className="form-message">
                                        {errors.personalAutoPolicies.message}
                                    </span>
                                )}
                            </div>
                        </li>
                        <li style={tableRowStyle}>
                            <fieldset className={classes(
                                "form-field checkbox-group old-style",
                                errors.commercialAutoCheck && "form-field--error")}
                                role="radiogroup"
                                style={columnTextStyle}
                            >
                                <div className="checkbox-wrapper">
                                    <div>
                                        <input
                                            id="commercialAutoCheck"
                                            type="checkbox"
                                            {...register("commercialAutoCheck")}
                                            onChange={(v) => {
                                                setCommercialAutoCheck(v.target.checked)
                                                const premiums = document.getElementById("commercialAutoPremium")
                                                const policies = document.getElementById("commercialAutoPolicies")
                                                if (premiums && policies && v.target.checked === false) {
                                                    premiums.value = "";
                                                    policies.value = "";
                                                    addPolicies();
                                                    addPremiums();
                                                }
                                            }}
                                        />
                                        <label htmlFor="commercialAutoCheck" className="checkbox">Commercial Auto (Excluding Trucking)</label>
                                        {errors.commercialAutoCheck && (
                                            <span className="form-message">
                                                {errors.commercialAutoCheck.message}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </fieldset>
                            <div
                                style={columnInputStyle}
                                className={classes(
                                    "form-field",
                                    errors.commercialAutoPremium && "form-field--error"
                                )}
                            >
                                <InputNumberFormat
                                    id="commercialAutoPremium"
                                    type="text"
                                    style={{ width: "98%" }}
                                    placeholder="$"
                                    disabled={!commercialAutoCheck}
                                    {...register("commercialAutoPremium")}
                                    onBlur={addPremiums}
                                    locales="en-US"
                                    format="currency"
                                    currency="USD"
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={2}
                                />
                                {errors.commercialAutoPremium && (
                                    <span className="form-message">
                                        {errors.commercialAutoPremium.message}
                                    </span>
                                )}
                            </div>
                            <div
                                style={columnInputStyle}
                                className={classes(
                                    "form-field",
                                    errors.commercialAutoPolicies && "form-field--error"
                                )}>
                                <InputNumberFormat
                                    id="commercialAutoPolicies"
                                    type="text"
                                    style={{ width: "98%" }}
                                    disabled={!commercialAutoCheck}
                                    {...register("commercialAutoPolicies")}
                                    onBlur={addPolicies}
                                    locales="en-US"
                                    format="decimal"
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={0}
                                />
                                {errors.commercialAutoPolicies && (
                                    <span className="form-message">
                                        {errors.commercialAutoPolicies.message}
                                    </span>
                                )}
                            </div>
                        </li>
                        <li style={tableRowStyle}>
                            <fieldset className={classes(
                                "form-field checkbox-group old-style",
                                errors.commercialTruckingCheck && "form-field--error")}
                                role="radiogroup"
                                style={columnTextStyle}
                            >
                                <div className="checkbox-wrapper">
                                    <div>
                                        <input
                                            id="commercialTruckingCheck"
                                            type="checkbox"
                                            {...register("commercialTruckingCheck")}
                                            onChange={(v) => {
                                                setCommercialTruckingCheck(v.target.checked)
                                                const premiums = document.getElementById("commercialTruckingPremium")
                                                const policies = document.getElementById("commercialTruckingPolicies")
                                                if (premiums && policies && v.target.checked === false) {
                                                    premiums.value = "";
                                                    policies.value = "";
                                                    addPolicies();
                                                    addPremiums();
                                                }
                                            }}
                                        />
                                        <label htmlFor="commercialTruckingCheck" className="checkbox">Commercial Trucking</label>
                                        {errors.commercialTruckingCheck && (
                                            <span className="form-message">
                                                {errors.commercialTruckingCheck.message}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </fieldset>
                            <div
                                style={columnInputStyle}
                                className={classes(
                                    "form-field",
                                    errors.commercialTruckingPremium && "form-field--error"
                                )}
                            >
                                <InputNumberFormat
                                    id="commercialTruckingPremium"
                                    type="text"
                                    style={{ width: "98%" }}
                                    placeholder="$"
                                    disabled={!commercialTruckingCheck}
                                    {...register("commercialTruckingPremium")}
                                    onBlur={addPremiums}
                                    locales="en-US"
                                    format="currency"
                                    currency="USD"
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={2}
                                />
                                {errors.commercialTruckingPremium && (
                                    <span className="form-message">
                                        {errors.commercialTruckingPremium.message}
                                    </span>
                                )}
                            </div>
                            <div
                                style={columnInputStyle}
                                className={classes(
                                    "form-field",
                                    errors.commercialTruckingPolicies && "form-field--error"
                                )}>
                                <InputNumberFormat
                                    id="commercialTruckingPolicies"
                                    type="text"
                                    style={{ width: "98%" }}
                                    disabled={!commercialTruckingCheck}
                                    {...register("commercialTruckingPolicies")}
                                    onBlur={addPolicies}
                                    locales="en-US"
                                    format="decimal"
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={0}
                                />
                                {errors.commercialTruckingPolicies && (
                                    <span className="form-message">
                                        {errors.commercialTruckingPolicies.message}
                                    </span>
                                )}
                            </div>
                        </li>
                        <li style={tableRowStyle}>
                            <fieldset className={classes(
                                "form-field checkbox-group old-style",
                                errors.generalCheck && "form-field--error")}
                                role="radiogroup"
                                style={columnTextStyle}
                            >
                                <div className="checkbox-wrapper">
                                    <div>
                                        <input
                                            id="generalCheck"
                                            type="checkbox"
                                            {...register("generalCheck")}
                                            onChange={(v) => {
                                                setGeneralCheck(v.target.checked)
                                                const premiums = document.getElementById("generalPremium")
                                                const policies = document.getElementById("generalPolicies")
                                                if (premiums && policies && v.target.checked === false) {
                                                    premiums.value = "";
                                                    policies.value = "";
                                                    addPolicies();
                                                    addPremiums();
                                                }
                                            }}
                                        />
                                        <label htmlFor="generalCheck" className="checkbox">BOP and General Liability</label>
                                        {errors.generalCheck && (
                                            <span className="form-message">
                                                {errors.generalCheck.message}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </fieldset>
                            <div
                                style={columnInputStyle}
                                className={classes(
                                    "form-field",
                                    errors.generalPremium && "form-field--error"
                                )}
                            >
                                <InputNumberFormat
                                    id="generalPremium"
                                    type="text"
                                    style={{ width: "98%" }}
                                    placeholder="$"
                                    disabled={!generalCheck}
                                    {...register("generalPremium")}
                                    onBlur={addPremiums}
                                    locales="en-US"
                                    format="currency"
                                    currency="USD"
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={2}
                                />
                                {errors.generalPremium && (
                                    <span className="form-message">
                                        {errors.generalPremium.message}
                                    </span>
                                )}
                            </div>
                            <div
                                style={columnInputStyle}
                                className={classes(
                                    "form-field",
                                    errors.generalPolicies && "form-field--error"
                                )}>
                                <InputNumberFormat
                                    id="generalPolicies"
                                    type="text"
                                    style={{ width: "98%" }}
                                    disabled={!generalCheck}
                                    {...register("generalPolicies")}
                                    onBlur={addPolicies}
                                    locales="en-US"
                                    format="decimal"
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={0}
                                />
                                {errors.generalPolicies && (
                                    <span className="form-message">
                                        {errors.generalPolicies.message}
                                    </span>
                                )}
                            </div>
                        </li>
                        <li style={tableRowStyle}>
                            <fieldset className={classes(
                                "form-field checkbox-group old-style",
                                errors.homeCheck && "form-field--error")}
                                role="radiogroup"
                                style={columnTextStyle}
                            >
                                <div className="checkbox-wrapper">
                                    <div>
                                        <input
                                            id="homeCheck"
                                            type="checkbox"
                                            {...register("homeCheck")}
                                            onChange={(v) => {
                                                setHomeCheck(v.target.checked)
                                                const premiums = document.getElementById("homePremium")
                                                const policies = document.getElementById("homePolicies")
                                                if (premiums && policies && v.target.checked === false) {
                                                    premiums.value = "";
                                                    policies.value = "";
                                                    addPolicies();
                                                    addPremiums();
                                                }
                                            }}
                                        />
                                        <label htmlFor="homeCheck" className="checkbox">Home, Condo, and Rental</label>
                                        {errors.homeCheck && (
                                            <span className="form-message">
                                                {errors.homeCheck.message}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </fieldset>
                            <div
                                style={columnInputStyle}
                                className={classes(
                                    "form-field",
                                    errors.homePremium && "form-field--error"
                                )}
                            >
                                <InputNumberFormat
                                    id="homePremium"
                                    type="text"
                                    style={{ width: "98%" }}
                                    placeholder="$"
                                    disabled={!homeCheck}
                                    {...register("homePremium")}
                                    onBlur={addPremiums}
                                    locales="en-US"
                                    format="currency"
                                    currency="USD"
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={2}
                                />
                                {errors.homePremium && (
                                    <span className="form-message">
                                        {errors.homePremium.message}
                                    </span>
                                )}
                            </div>
                            <div
                                style={columnInputStyle}
                                className={classes(
                                    "form-field",
                                    errors.homePolicies && "form-field--error"
                                )}>
                                <InputNumberFormat
                                    id="homePolicies"
                                    type="text"
                                    style={{ width: "98%" }}
                                    disabled={!homeCheck}
                                    {...register("homePolicies")}
                                    onBlur={addPolicies}
                                    locales="en-US"
                                    format="decimal"
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={0}
                                />
                                {errors.homePolicies && (
                                    <span className="form-message">
                                        {errors.homePolicies.message}
                                    </span>
                                )}
                            </div>
                        </li>
                        <li style={tableRowStyle}>
                            <fieldset className={classes(
                                "form-field checkbox-group old-style",
                                errors.recreationalCheck && "form-field--error")}
                                role="radiogroup"
                                style={columnTextStyle}
                            >
                                <div className="checkbox-wrapper">
                                    <div>
                                        <input
                                            id="recreationalCheck"
                                            type="checkbox"
                                            {...register("recreationalCheck")}
                                            onChange={(v) => {
                                                setRecreationalCheck(v.target.checked)
                                                const premiums = document.getElementById("recreationalPremium")
                                                const policies = document.getElementById("recreationalPolicies")
                                                if (premiums && policies && v.target.checked === false) {
                                                    premiums.value = "";
                                                    policies.value = "";
                                                    addPolicies();
                                                    addPremiums();
                                                }
                                            }}
                                        />
                                        <label htmlFor="recreationalCheck" className="checkbox">Recreational</label>
                                        {errors.recreationalCheck && (
                                            <span className="form-message">
                                                {errors.recreationalCheck.message}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </fieldset>
                            <div
                                style={columnInputStyle}
                                className={classes(
                                    "form-field",
                                    errors.recreationalPremium && "form-field--error"
                                )}
                            >
                                <InputNumberFormat
                                    id="recreationalPremium"
                                    type="text"
                                    style={{ width: "98%" }}
                                    placeholder="$"
                                    disabled={!recreationalCheck}
                                    {...register("recreationalPremium")}
                                    onBlur={addPremiums}
                                    locales="en-US"
                                    format="currency"
                                    currency="USD"
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={2}
                                />
                                {errors.recreationalPremium && (
                                    <span className="form-message">
                                        {errors.recreationalPremium.message}
                                    </span>
                                )}
                            </div>
                            <div
                                style={columnInputStyle}
                                className={classes(
                                    "form-field",
                                    errors.recreationalPolicies && "form-field--error"
                                )}>
                                <InputNumberFormat
                                    id="recreationalPolicies"
                                    type="text"
                                    style={{ width: "98%" }}
                                    disabled={!recreationalCheck}
                                    {...register("recreationalPolicies")}
                                    onBlur={addPolicies}
                                    locales="en-US"
                                    format="decimal"
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={0}
                                />
                                {errors.recreationalPolicies && (
                                    <span className="form-message">
                                        {errors.recreationalPolicies.message}
                                    </span>
                                )}
                            </div>
                        </li>
                        <li style={tableRowStyle}>
                            <fieldset className={classes(
                                "form-field checkbox-group old-style",
                                errors.otherCheck && "form-field--error")}
                                role="radiogroup"
                                style={columnTextStyle}
                            >
                                <div className="checkbox-wrapper">
                                    <div>
                                        <input
                                            id="otherCheck"
                                            type="checkbox"
                                            {...register("otherCheck")}
                                            onChange={(v) => {
                                                setOtherCheck(v.target.checked)
                                                const premiums = document.getElementById("otherPremium")
                                                const policies = document.getElementById("otherPolicies")
                                                if (premiums && policies && v.target.checked === false) {
                                                    premiums.value = "";
                                                    policies.value = "";
                                                    addPolicies();
                                                    addPremiums();
                                                }
                                            }}
                                        />
                                        <label htmlFor="otherCheck" className="checkbox">Other</label>
                                        {errors.otherCheck && (
                                            <span className="form-message">
                                                {errors.otherCheck.message}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </fieldset>
                            <div
                                style={columnInputStyle}
                                className={classes(
                                    "form-field",
                                    errors.otherPremium && "form-field--error"
                                )}
                            >
                                <InputNumberFormat
                                    id="otherPremium"
                                    type="text"
                                    style={{ width: "98%" }}
                                    placeholder="$"
                                    disabled={!otherCheck}
                                    {...register("otherPremium")}
                                    onBlur={addPremiums}
                                    locales="en-US"
                                    format="currency"
                                    currency="USD"
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={2}
                                />
                                {errors.otherPremium && (
                                    <span className="form-message">
                                        {errors.otherPremium.message}
                                    </span>
                                )}
                            </div>
                            <div
                                style={columnInputStyle}
                                className={classes(
                                    "form-field",
                                    errors.otherPolicies && "form-field--error"
                                )}>
                                <InputNumberFormat
                                    id="otherPolicies"
                                    type="text"
                                    style={{ width: "98%" }}
                                    disabled={!otherCheck}
                                    {...register("otherPolicies")}
                                    onBlur={addPolicies}
                                    locales="en-US"
                                    format="decimal"
                                    minimumFractionDigits={0}
                                    maximumFractionDigits={0}
                                />
                                {errors.otherPolicies && (
                                    <span className="form-message">
                                        {errors.otherPolicies.message}
                                    </span>
                                )}
                            </div>
                        </li>
                        <li style={tableRowStyle}>
                            <div style={columnTextStyle} />
                            <div style={columnTextStyle}>
                                <label><b>Total Annual Written Premium</b></label>
                                <p>Prior Year or Forecasted</p>
                                <input
                                    id="totalPremium"
                                    type="text"
                                    style={{ width: "98%" }}
                                    placeholder="$"
                                    value={totalPremium}
                                    disabled={true}
                                    {...register("totalPremium")}
                                />
                            </div>
                            <div style={columnTextStyle}>
                                <label><b>Total Policies in Force (PIF)</b></label>
                                <p>Prior Year or Forecasted</p>
                                <input
                                    id="totalPolicies"
                                    type="text"
                                    style={{ width: "98%" }}
                                    placeholder="$"
                                    value={totalPolicies}
                                    disabled={true}
                                    {...register("totalPolicies")}
                                />
                            </div>
                        </li>
                    </ul>
                </div>
                <label className="text" style={{ marginBottom: "3rem" }}>
                    Your Top Three Insurers Sold by % of Total Personal and Commercial Written Premium{/*
                    */}<span className='required'>*</span>
                </label>
                <div>
                    <div style={tableHeaderStyle}>
                        <label className="text" style={tableHeaderTextStyle}>
                            Company
                        </label>
                        <label className="text" style={tableHeaderTextStyle}>
                            % Personal
                        </label>
                        <label className="text" style={tableHeaderTextStyle}>
                            % Commercial
                        </label>
                    </div>
                    <ul className="list list--one-column">
                        <li style={tableRowStyle}>
                            <div
                                className={classes(
                                    "form-field",
                                    errors.insurerOne && "form-field--error"
                                )}
                                style={columnInputStyle}
                            >
                                <div className="select-box" style={{ width: "98%" }}>
                                    <OptionsProvider
                                        options={insurersOptions}
                                        defaultOption={"Please Select"}
                                        tailOptions={["OTHER", "NA"]}
                                        register={register}
                                        registerValue={"insurerOne"}
                                        id="insurerOne"
                                    />
                                </div>
                                {errors.insurerOne && (
                                    <span className="form-message">
                                        {errors.insurerOne.message}
                                    </span>
                                )}
                            </div>
                            <div
                                style={columnInputStyle}
                                className={classes(
                                    "form-field",
                                    errors.personalOne && "form-field--error"
                                )}
                            >
                                <input
                                    id="personalOne"
                                    type="text"
                                    style={{ width: "98%" }}
                                    placeholder="%"
                                    {...register("personalOne")}
                                />
                                {errors.personalOne && (
                                    <span className="form-message">
                                        {errors.personalOne.message}
                                    </span>
                                )}
                            </div>
                            <div
                                style={columnInputStyle}
                                className={classes(
                                    "form-field",
                                    errors.commercialOne && "form-field--error"
                                )}
                            >
                                <input
                                    id="commercialOne"
                                    type="text"
                                    style={{ width: "98%" }}
                                    placeholder="%"
                                    {...register("commercialOne")}
                                />
                                {errors.commercialOne && (
                                    <span className="form-message">
                                        {errors.commercialOne.message}
                                    </span>
                                )}
                            </div>
                        </li>
                        <li style={tableRowStyle}>
                            <div
                                className={classes(
                                    "form-field",
                                    errors.insurerTwo && "form-field--error"
                                )}
                                style={columnInputStyle}
                            >
                                <div className="select-box" style={{ width: "98%" }}>
                                    <OptionsProvider
                                        options={insurersOptions}
                                        defaultOption={"Please Select"}
                                        tailOptions={["OTHER", "NA"]}
                                        register={register}
                                        registerValue={"insurerTwo"}
                                        id="insurerTwo"
                                    />
                                </div>
                                {errors.insurerTwo && (
                                    <span className="form-message">
                                        {errors.insurerTwo.message}
                                    </span>
                                )}
                            </div>
                            <div
                                style={columnInputStyle}
                                className={classes(
                                    "form-field",
                                    errors.personalTwo && "form-field--error"
                                )}
                            >
                                <input
                                    id="personalTwo"
                                    type="text"
                                    style={{ width: "98%" }}
                                    placeholder="%"
                                    {...register("personalTwo")}
                                />
                                {errors.personalTwo && (
                                    <span className="form-message">
                                        {errors.personalTwo.message}
                                    </span>
                                )}
                            </div>
                            <div
                                style={columnInputStyle}
                                className={classes(
                                    "form-field",
                                    errors.commercialTwo && "form-field--error"
                                )}
                            >
                                <input
                                    id="commercialTwo"
                                    type="text"
                                    style={{ width: "98%" }}
                                    placeholder="%"
                                    {...register("commercialTwo")}
                                />
                                {errors.commercialTwo && (
                                    <span className="form-message">
                                        {errors.commercialTwo.message}
                                    </span>
                                )}
                            </div>
                        </li>
                        <li style={tableRowStyle}>
                            <div
                                className={classes(
                                    "form-field",
                                    errors.insurerThree && "form-field--error"
                                )}
                                style={columnInputStyle}
                            >
                                <div className="select-box" style={{ width: "98%" }}>
                                    <OptionsProvider
                                        options={insurersOptions}
                                        defaultOption={"Please Select"}
                                        tailOptions={["OTHER", "NA"]}
                                        register={register}
                                        registerValue={"insurerThree"}
                                        id="insurerThree"
                                    />
                                </div>
                                {errors.insurerThree && (
                                    <span className="form-message">
                                        {errors.insurerThree.message}
                                    </span>
                                )}
                            </div>
                            <div
                                style={columnInputStyle}
                                className={classes(
                                    "form-field",
                                    errors.personalThree && "form-field--error"
                                )}
                            >
                                <input
                                    id="personalThree"
                                    type="text"
                                    style={{ width: "98%" }}
                                    placeholder="%"
                                    {...register("personalThree")}
                                />
                                {errors.personalThree && (
                                    <span className="form-message">
                                        {errors.personalThree.message}
                                    </span>
                                )}
                            </div>
                            <div
                                style={columnInputStyle}
                                className={classes(
                                    "form-field",
                                    errors.commercialThree && "form-field--error"
                                )}
                            >
                                <input
                                    id="commercialThree"
                                    type="text"
                                    style={{ width: "98%" }}
                                    placeholder="%"
                                    {...register("commercialThree")}
                                />
                                {errors.commercialThree && (
                                    <span className="form-message">
                                        {errors.commercialThree.message}
                                    </span>
                                )}
                            </div>
                        </li>
                    </ul>
                </div>

                <p>
                    <span className='required'>*</span> Indicates required field.
                </p>

                <div>
                    <button
                        className="btn btn--primary btn--full-mobile btn--pull-right"
                        type="submit"
                        style={buttonStyle}
                        disabled={buttonsDisabled || interviewIsCompleted}
                    >
                        <span>Next</span>
                    </button>
                    <button
                        id="agent-search-clear-button"
                        type="button"
                        className="btn btn--secondary btn--full-mobile btn--pull-right"
                        style={buttonStyle}
                        onClick={onSaveAndExit}
                        disabled={buttonsDisabled || interviewIsCompleted}
                    >
                        <span>Save and Exit</span>
                    </button>
                </div>
            </form>
        </GeaoVerticalStepperLayout>
    );
}
